import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, TrendingUp, TrendingDown, IndianRupee, ArrowRight } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, inputCls } from '../../../components/admin/billing/shared';
import { SourceToggle } from './_shared';
import EmailReportButton from './EmailReportButton';

const FinanceReportsPage = () => {
  const { authHeader } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [source, setSource] = useState('gst');
  const [pnl, setPnl] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: t }] = await Promise.all([
        axios.get(`${API}/reports/pnl?year=${year}&month=${month}&source=${source}`, { headers: authHeader() }),
        axios.get(`${API}/reports/expense-trend?months=6`, { headers: authHeader() }),
      ]);
      setPnl(p); setTrend(t);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, year, month, source]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const profitColor = (n) => (n >= 0 ? 'text-emerald-700' : 'text-red-600');
  const ProfitIcon = pnl && pnl.profit.gross >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="finance-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <IndianRupee className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Finance Reports</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inputCls} data-testid="report-year">
            {Array.from({ length: 5 }).map((_, i) => {
              const y = today.getFullYear() - 2 + i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={inputCls} data-testid="report-month">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <Link to="/admin/reports" className="text-xs text-teal-700 hover:underline whitespace-nowrap">Inventory Reports →</Link>
          <EmailReportButton
            reportType="finance"
            getFilters={() => ({ year, month, months: 6, source })}
          />
        </div>
      </div>

      <SourceToggle value={source} onChange={setSource} idPrefix="finrep-source" />

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      {loading ? (
        <div className="text-center py-12 text-slate-500"><Loader2 className="inline animate-spin mr-2" size={20} /> Loading…</div>
      ) : pnl ? (
        <>
          {/* Headline cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Income (Billed{source === 'gst' ? ' · GST books' : source === 'legacy' ? ' · Legacy' : ''})</div>
              <div className="text-2xl font-bold font-mono text-slate-900">{fmtINR(pnl.income.billed)}</div>
              <div className="text-[11px] text-slate-500 mt-1">{pnl.income.invoice_count} invoices · {fmtINR(pnl.income.received)} received</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Outstanding</div>
              <div className="text-2xl font-bold font-mono text-amber-700">{fmtINR(pnl.income.outstanding)}</div>
              <div className="text-[11px] text-slate-500 mt-1">unpaid customer dues</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Expenses</div>
              <div className="text-2xl font-bold font-mono text-red-600">{fmtINR(pnl.expenses.total)}</div>
              <div className="text-[11px] text-slate-500 mt-1">{pnl.expenses.by_category.length} categories</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Gross Profit</div>
              <div className={`text-2xl font-bold font-mono ${profitColor(pnl.profit.gross)} flex items-center gap-1`}>
                <ProfitIcon size={20} />{fmtINR(pnl.profit.gross)}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Net realized: {fmtINR(pnl.profit.net_realized)}</div>
            </div>
          </div>

          {/* Category breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <h2 className="text-sm font-semibold">Expenses by Category</h2>
                <span className="text-xs text-slate-500">{fmtINR(pnl.expenses.total)} total</span>
              </div>
              <div className="p-4 space-y-2">
                {pnl.expenses.by_category.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No expenses recorded for this month.</p>
                ) : pnl.expenses.by_category.map((c) => {
                  const pct = pnl.expenses.total > 0 ? (c.total / pnl.expenses.total) * 100 : 0;
                  return (
                    <div key={c.category} data-testid={`cat-${c.category}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-700">{c.category}</span>
                        <span className="font-mono text-slate-900">{fmtINR(c.total)} <span className="text-[11px] text-slate-400">({pct.toFixed(1)}%)</span></span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Trend */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <h2 className="text-sm font-semibold">Expense Trend — last 6 months</h2>
              </div>
              <div className="p-4">
                {!trend || trend.months.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No trend data yet.</p>
                ) : (
                  (() => {
                    const max = Math.max(...trend.months.map((m) => m.total), 1);
                    return (
                      <div className="flex items-end gap-2 h-40">
                        {trend.months.map((m) => {
                          const h = max > 0 ? (m.total / max) * 100 : 0;
                          return (
                            <div key={m.label} className="flex-1 flex flex-col items-center justify-end gap-1" data-testid={`bar-${m.label}`}>
                              <span className="text-[10px] font-mono text-slate-600 whitespace-nowrap">{fmtINR(m.total).replace('₹', '')}</span>
                              <div className="w-full bg-teal-100 rounded-t" style={{ height: `${h}%`, minHeight: m.total > 0 ? '6px' : '2px' }}>
                                <div className="w-full h-full bg-gradient-to-t from-teal-500 to-teal-400 rounded-t" />
                              </div>
                              <span className="text-[10px] text-slate-500 mt-1">{m.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>

          {/* Cross-link */}
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex items-center justify-between">
            <div className="text-sm text-teal-800">
              Want per-staff incentive + base + OT breakdown? Use the Payroll page.
            </div>
            <Link to="/admin/payroll" className="text-sm bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
              Go to Payroll <ArrowRight size={14} />
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default FinanceReportsPage;
